import React, { Fragment, useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useCurrentOrgId, useIsAdmin } from "../../../auth/authUtils";
import {
  useContentInProcessing,
  useAcknowledgeFailure,
} from "../../creatorServices";
import { useSearchQuery } from "../../common/use-searchQuery";
import { useSelected } from "../OrganizeProvider";
import { anyTrue, isBlank, notBlank } from "../../../util/ext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faCircleX,
  faRotateRight,
} from "@fortawesome/pro-solid-svg-icons";
import {
  add,
  append,
  equals,
  filter,
  find,
  length,
  lensProp,
  map,
  mergeRight,
  pipe,
  prop,
  propEq,
  reduce,
  reject,
  set,
  view,
} from "ramda";
import {
  contentProcessingStatusTerms,
  contentTypeTerms,
  getContentProcessingStatusLabel,
  getContentTypeLabel,
} from "../../contentUtils";
import { useQueryClient } from "react-query";
import { useMediaQuery } from "@material-ui/core";

import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import ContentFacetMenu from "../../content/browse/ContentFacetMenu";
import ContentSearchBar from "../../content/browse/ContentSearchBar";
import ContentSourcesAutocomplete from "../common/ContentSourcesAutocomplete";
import ConfirmButton from "../../../components/ConfirmButton";
import EditSearchModal from "../../common/EditSearchModal";
import MediaBrowser from "../../common/MediaBrowser";
import OrganizeProcessingTable from "./OrganizeProcessingTable";
import QueryPills from "../../common/QueryPills";
import SelectedFilesCheckbox from "../files/SelectedFilesCheckbox";
import { useReprocessSubmission } from "../upload/uploadServices";
import { getOrgIdForProcessing } from "./processingUtils";

const getQueryParams = map(prop("queryParam"));

const availableFilters = [
  {
    name: "type",
    displayName: "Type",
    terms: contentTypeTerms.terms,
  },
  {
    name: "status",
    displayName: "Status",
    terms: contentProcessingStatusTerms.terms,
  },
];

const useStyles = makeStyles(theme => ({
  searchBar: {
    paddingTop: 15,
    paddingBottom: 15,
  },

  titleActions: {
    height: 50,
    paddingTop: 10,
    [theme.breakpoints.up("md")]: {
      paddingTop: 0,
    },
  },
}));

const initialSearchState = {
  appliedQuery: [],
  source: {
    projectKey: "",
    projectName: "",
  },
  statuses: [],
  types: [],
  page: 0,
  limit: 10,
  orderBy: "uploadDate",
  orderDirection: "desc",
};

const OrganizeProcessingBrowser = props => {
  const classes = useStyles();
  const isAdmin = useIsAdmin();
  const currentOrgId = useCurrentOrgId();

  const [scrollVisible, setScrollVisible] = useState(true);
  const { removeAllSelectedContent, selectedContent } = useSelected();
  const isXs = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const betweenSmAndMed = useMediaQuery(
    "(min-width:1300px) and (max-width:1440px) ",
  );
  const queryClient = useQueryClient();

  const query = useSearchQuery(initialSearchState);
  const [queryState, setQueryState] = useState({
    limit: initialSearchState.limit,
    offset: query.state.limit * query.state.page,
    sort: `${query.state.orderBy} ${query.state.orderDirection}`,
  });

  useEffect(() => {
    const { appliedQuery, source, statuses, types, limit, page } = query.state;

    const sort =
      query.state.orderBy === null
        ? `${initialSearchState.orderBy},${initialSearchState.orderDirection}`
        : `${query.state.orderBy},${query.state.orderDirection}`;

    setQueryState({
      limit,
      page,
      sort,
      query: appliedQuery,
      source: source.projectKey || source.projectName,
      statuses: statuses.join(","),
      contentTypes: types.join(","),
    });

    setScrollVisible(true);
  }, [query.state]);

  const results = useContentInProcessing(currentOrgId, queryState);
  const [content, setContent] = useState({
    all: [],
    failed: [],
  });
  const [count, setCount] = useState("-");
  useEffect(() => {
    if (results.isSuccess) {
      const all = map(
        item => set(lensProp("id"), view(lensProp("mediaId"), item), item),
        results.data.content,
      );
      setContent({
        all,
        failed: filter(propEq("status", "FAILURE"), all),
      });
      setCount(results.data.totalElements);
    }
  }, [results.isSuccess, results.data, results.isFetching]);

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  //-- Reprocess
  const reprocess = useReprocessSubmission();
  const reprocessAllSelected = () => {
    if (notBlank(selectedContent) && isAdmin) {
      return Promise.all(
        map(
          content =>
            reprocess.mutateAsync({
              resourceIds: {
                submissionId: content.mediaId,
              },
            }),
          selectedContent,
        ),
      );
    }
  };

  //-- Handle Clearing Content
  const acknowledgeFailure = useAcknowledgeFailure();
  const clearAllSelected = () => {
    if (notBlank(selectedContent)) {
      return Promise.all(
        map(content => {
          const orgId = isAdmin ? getOrgIdForProcessing(content) : currentOrgId;
          if (orgId) {
            return acknowledgeFailure.mutateAsync({
              resourceIds: {
                orgId: orgId,
                submissionId: content.mediaId,
              },
            });
          } else {
            // if no org id found, just resolve
            return Promise.resolve();
          }
        }, selectedContent),
      );
    }
  };

  const clearAllFailed = () => {
    if (notBlank(content.failed)) {
      return Promise.all(
        map(content => {
          const orgId = isAdmin ? getOrgIdForProcessing(content) : currentOrgId;
          if (orgId) {
            return acknowledgeFailure.mutateAsync({
              resourceIds: {
                orgId: orgId,
                submissionId: content.mediaId,
              },
            });
          } else {
            // if no org id found, just resolve
            return Promise.resolve();
          }
        }, selectedContent),
      );
    }
  };

  //-- Manage Filters
  const [selectedFilters, setSelectedFilters] = useState([]);
  useEffect(() => {
    const types = map(
      type => ({
        label: getContentTypeLabel(type),
        queryParam: type,
      }),
      query.state.types,
    );
    const statuses = map(
      status => ({
        label: getContentProcessingStatusLabel(status),
        queryParam: status,
      }),
      query.state.statuses,
    );

    setSelectedFilters([
      { name: "type", displayName: "Type", selectedTerms: types },
      { name: "status", displayName: "Status", selectedTerms: statuses },
    ]);
  }, [query.state]);

  const handleFilterSelected = filter => {
    const value = getQueryParams(filter.terms);

    if (filter.name === "type") {
      query.handleUpdateTypes(value);
    }

    if (filter.name === "status") {
      query.handleUpdateStatuses(value);
    }

    removeAllSelectedContent();
  };

  const handleClearAllFilters = () => {
    query.handleUpdateTypes([]);
    query.handleUpdateStatuses([]);
    removeAllSelectedContent();
  };

  //-- Manage nested menu items
  const [selectedFilter, setSelectedFilter] = useState("");
  const handleFilterClick = filterName => {
    equals(selectedFilter, filterName)
      ? setSelectedFilter("")
      : setSelectedFilter(filterName);
  };

  //-- Handle Search
  //-- Replace Search Term in Query Pill
  const [editSearchModal, setEditSearchModal] = useState(false);
  const [previousSearchTerm, setPreviousSearchTerm] = useState("");
  const closeSearchModal = () => setEditSearchModal(false);
  const handleQueryPillClick = queryPart => {
    setEditSearchModal(true);
    setPreviousSearchTerm(queryPart);
  };
  const handleFilterDelete = useCallback(
    value => {
      query.handleUpdateQuery(reject(equals(value), query.state.appliedQuery));
    },
    [query],
  );

  const handleEditSearchApply = useCallback(
    newSearchTerm => {
      const appliedQuery = query.state.appliedQuery;

      const withoutPreviousTerm = reject(
        equals(previousSearchTerm),
        appliedQuery,
      );

      const updatedQuery = isBlank(newSearchTerm)
        ? withoutPreviousTerm
        : append(newSearchTerm, withoutPreviousTerm);

      query.handleUpdateQuery(updatedQuery);

      closeSearchModal();
      setPreviousSearchTerm("");
    },
    [previousSearchTerm, query],
  );

  return (
    <Fragment>
      <EditSearchModal
        editSearchModal={editSearchModal}
        previousSearchTerm={previousSearchTerm}
        handleEditSearchModalClose={closeSearchModal}
        handleEditSearchApply={handleEditSearchApply}
      />
      <MediaBrowser
        loaded={results.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <Grid
            className={classes.searchBar}
            container
            alignItems={isXs ? "flex-start" : "center"}
            justify="space-between"
          >
            <Grid
              item
              xs={6}
              container
              alignItems="center"
              className={classes.titleActions}
            >
              <Grid item>
                <Typography
                  variant="h2"
                  style={{ paddingTop: 8, paddingBottom: 8 }}
                >
                  Processing ({count})
                </Typography>
              </Grid>

              <Grid item>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() =>
                      queryClient.invalidateQueries("content-processing")
                    }
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                  </IconButton>
                </Tooltip>
              </Grid>

              {results.isSuccess && (
                <Fragment>
                  {notBlank(content.failed) && (
                    <Grid item>
                      <ConfirmButton
                        variant="failed"
                        icon={faCircleX}
                        tooltipTitle="Clear All Failed"
                        handleDelete={clearAllFailed}
                        handleSuccess={() => removeAllSelectedContent()}
                        dialogTitle="Clear All Failed"
                        dialogConfirmText="Clear"
                        dialogSuccessMessage="Failed Items Cleared"
                        dialogBody="Clear all the failed items from processing?"
                      />
                    </Grid>
                  )}

                  <Fragment>
                    {notBlank(selectedContent) && (
                      <Fragment>
                        {isAdmin && (
                          <Grid item>
                            <ConfirmButton
                              variant="darker"
                              icon={faArrowsRotate}
                              tooltipTitle="Reprocess All Selected"
                              handleDelete={reprocessAllSelected}
                              handleSuccess={() => removeAllSelectedContent()}
                              dialogTitle="Reprocess All Selected"
                              dialogConfirmText="Reprocess"
                              dialogSuccessMessage="Items Reprocessing"
                              dialogBody="Reprocess all the selected items from the processing?"
                            />
                          </Grid>
                        )}

                        <Grid item>
                          <ConfirmButton
                            variant="darker"
                            icon={faCircleX}
                            tooltipTitle="Clear All Selected"
                            handleDelete={clearAllSelected}
                            handleSuccess={() => removeAllSelectedContent()}
                            dialogTitle="Clear All Selected"
                            dialogConfirmText="Clear"
                            dialogSuccessMessage="Items Cleared"
                            dialogBody="Clear all the selected items from the processing?"
                          />
                        </Grid>
                      </Fragment>
                    )}

                    {notBlank(selectedContent) && (
                      <Grid item>
                        <SelectedFilesCheckbox
                          title={"Unselect All"}
                          isPagedContent
                          total={selectedContent.length}
                          content={selectedContent}
                        />
                      </Grid>
                    )}

                    {/* {notBlank(content.failed) && (
                      <Grid item>
                        <SelectedFilesCheckbox
                          title={"Unselect All Failed"}
                          isPagedContent
                          total={content.failed.length}
                          content={content.failed}
                        />
                      </Grid>
                    )} */}
                  </Fragment>
                </Fragment>
              )}
            </Grid>

            <Grid item container xs={6} justify="flex-end" alignItems="center">
              <Grid item xs={12} sm={betweenSmAndMed ? "auto" : 12} md={6}>
                <ContentSearchBar
                  query={query.state.appliedQuery}
                  onQueryChange={query.handleUpdateQuery}
                />
              </Grid>
              <Grid item xs="auto" style={{ padding: "0 10px" }}>
                <ContentSourcesAutocomplete
                  setSource={query.handleUpdateSource}
                  source={query.state.source}
                />
              </Grid>
              <Grid item xs="auto">
                <ContentFacetMenu
                  useCustomStatusFacet={false}
                  facets={map(filter => {
                    const filterType = find(
                      propEq("name", filter.name),
                      selectedFilters,
                    );
                    // selected & selectedTerms tracks total to display for each autocomplete
                    return mergeRight(filter, {
                      selected: filterType?.selectedTerms.length > 0,
                      selectedTerms: filterType?.selectedTerms ?? [],
                    });
                  }, availableFilters)}
                  handleClearFilters={handleClearAllFilters}
                  handleFacetClick={handleFilterClick}
                  handleFacetSelection={handleFilterSelected}
                  loading={results.isLoading}
                  selectedFacet={selectedFilter}
                  selectedFacets={selectedFilters}
                  totalSelectedTerms={pipe(
                    map(prop("selectedTerms")),
                    map(length),
                    reduce(add, 0),
                  )(selectedFilters)}
                />
              </Grid>
            </Grid>

            <Grid container item alignItems="center">
              <QueryPills
                appliedQuery={query.state.appliedQuery}
                handleClick={handleQueryPillClick}
                handleDelete={handleFilterDelete}
              />
            </Grid>
          </Grid>
        }
        mediaTable={
          <OrganizeProcessingTable
            loading={anyTrue([
              results.isLoading,
              results.isFetching,
              acknowledgeFailure.isLoading,
            ])}
            total={results.isSuccess ? results.data.totalElements : 0}
            contentList={results.isSuccess ? content.all : []}
            page={query.state.page}
            orderBy={query.state.orderBy}
            order={query.state.orderDirection}
            onPageChange={query.handleUpdatePage}
            onSortChange={query.handleUpdateSort}
            onScrollVisible={handleScrollVisible}
            onRowsPerPageChange={query.handleUpdateLimit}
            rowsPerPage={query.state.limit}
            itemsPerPage={results.isSuccess ? results.data.size : 0}
          />
        }
      />
    </Fragment>
  );
};

export default OrganizeProcessingBrowser;
